import React from 'react';
import cx from 'classnames';
import Img from 'next/image';
import useThemeBlock from '@/hooks/useThemeBlock';
import block from './block.json';
// styles
import { getStyles } from './styles.css';

interface ForwardedImage
	extends React.ForwardRefExoticComponent<
			ImageProps & React.RefAttributes<HTMLImageElement>
		>,
		BlockConfigs {}

/**
 * COMPONENT
 */
export const Image = React.forwardRef<HTMLImageElement, ImageProps>(
	(
		{
			alt,
			src,
			width,
			height,
			caption,
			isSticky = false,
			className = '',
			sizes,
			priority = false,
			quality = 95,
			layout,
			style,
			theme = {},
		},
		ref
	) => {
		if (!src) return null;

		const { variant, blockTheme } = useThemeBlock(block.slug, theme);

		const rootClass = cx(
			'supt-figure',
			getStyles(blockTheme),
			isSticky && 'supt-isSticky',
			className
		);

		return (
			<figure
				ref={ref}
				className={rootClass}
				data-block={block.slug}
				data-variant={variant}
				style={style}
			>
				{!width && !height && layout !== 'fill' ? (
					// no need to optimise (we are either rendering an svg or outside of Next.js context)
					<img
						src={src as string}
						alt={alt ?? ''}
						className="supt-figure__image"
					/>
				) : (
					<Img
						src={src as string}
						alt={alt ?? ''}
						className="supt-figure__image"
						width={layout === 'fill' ? null : width}
						height={layout === 'fill' ? null : height}
						sizes={sizes}
						layout={layout}
						priority={priority}
						quality={quality}
						unoptimized={
							(src as string)?.endsWith?.('.svg') ||
							process.env.NEXT_PUBLIC_IS_THIS_NEXT !== 'true' // Only optimise image if in Nextjs (avoids Storybook errors)
						}
					/>
				)}
				{caption && (
					<figcaption className="supt-figure__figcaption">
						{caption}
					</figcaption>
				)}
			</figure>
		);
	}
) as ForwardedImage;

Image.slug = block.slug;
Image.title = block.title;
